import React from "react";
import ReactMarkdown, { type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Message } from "../types";
import { useTranslation } from "lib/i18n/use-translation";
import OriMessageFeedback from "./OriMessageFeedback";
import OriRecommendations from "./OriRecommendations";
import styles from "./OriMessageList.module.scss";

interface OriMessageBubbleProps {
  message: Message;
  // Suppressed while this bubble is the one Ori is still streaming into.
  showFeedback?: boolean;
}

type OriMarkdownTableProps = React.ComponentPropsWithRef<"table"> & ExtraProps;

export const OriMarkdownTable: React.FC<OriMarkdownTableProps> = ({
  children,
  node: _node,
  ...props
}) => {
  const { t } = useTranslation(["ori"]);
  return (
    <div
      aria-label={t("ori:chat.table.a11y")}
      className={styles.tableScroll}
      role="region"
      tabIndex={0}
    >
      <table {...props}>{children}</table>
    </div>
  );
};

// Memoized: during streaming, setMessages fires per chunk and re-renders the
// whole list — without this, every prior bubble re-parses its markdown each
// chunk. Stable historical bubbles keep their reference and skip the re-render.
const OriMessageBubble: React.FC<OriMessageBubbleProps> = React.memo(
  ({ message, showFeedback = false }) => {
    if (message.role === "assistant") {
      return (
        <div className={styles.assistantRow}>
          <div className={styles.assistantBubble}>
            <div className={styles.assistantText}>
              <ReactMarkdown
                components={{ table: OriMarkdownTable }}
                remarkPlugins={[remarkGfm]}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            {message.embedded_ui?.recommendations?.length ? (
              <OriRecommendations
                recommendations={message.embedded_ui.recommendations}
                configurationId={message.configuration_id}
                configurationName={message.configuration_name}
              />
            ) : null}
            {showFeedback && message.message_id ? (
              <OriMessageFeedback
                messageId={message.message_id}
                content={message.content}
                initialFeedback={message.user_feedback}
              />
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.userRow}>
        <div className={styles.userBubble}>{message.content}</div>
      </div>
    );
  }
);

OriMessageBubble.displayName = "OriMessageBubble";

export default OriMessageBubble;
